import { useEffect, useMemo } from 'react';
import { APIError, ApiErrors } from '@interxfi/api-client/api-error';
import { useAddToast } from '@interxfi/design-system/components/toast/toast-context';

type ErrorMessageGetter = (error: any) => string | undefined;

export const defaultStatusCodeMessages: Record<string, string> = {
  '500': 'Internal server error, please try your request again later',
  '503': 'Service temporarily unavailable, please try your request again later',
};

export function buildDefaultErrorMessageGetter(message: string | undefined, action?: string): ErrorMessageGetter {
  const messagePrepend = message && !action ? `${message}: ` : '';
  const actionAppend = action ? ` to ${action}` : '';

  return (error: any) => {
    if (!error) {
      return undefined;
    }

    if (error instanceof APIError) {
      const toLower = error.message.toLowerCase();

      if (toLower.includes(ApiErrors.TenantNotPermitted)) {
        return `${messagePrepend}You do not have permission${actionAppend}`;
      }

      if (toLower.includes(ApiErrors.RequiresClearingTenant)) {
        return `${messagePrepend}You must be using a clearing tenant${actionAppend}`;
      }

      if (toLower.includes(ApiErrors.RequiresProgramTenant)) {
        return `${messagePrepend}You must be using a program tenant${actionAppend}`;
      }

      if (toLower.includes(ApiErrors.RequiresClearingOrProgramTenant)) {
        return `${messagePrepend}You must be using a program or clearing tenant${actionAppend}`;
      }

      if (toLower.includes(ApiErrors.InvalidCredentials)) {
        return 'Invalid credentials';
      }
    }

    const msg: string | undefined = message || error.message;

    return defaultStatusCodeMessages[msg?.toString() as string] || msg;
  };
}

export function useErrorHandler(error: any, messageOrGetter?: string | ErrorMessageGetter) {
  const addToast = useAddToast();
  const messageGetter = useMemo(
    () => (typeof messageOrGetter === 'function' ? messageOrGetter : buildDefaultErrorMessageGetter(messageOrGetter)),
    [messageOrGetter],
  );

  useEffect(() => {
    if (error) {
      const message = messageGetter(error);

      if (message) {
        addToast({
          id: message || error.message || error instanceof APIError ? error.requestIdentifier : new Date().toISOString(),
          variant: 'error',
          content: message,
        });
      }
    }
  }, [addToast, error, messageGetter]);
}
